import { useCallback, useEffect, useState } from "react";
import {
  EmailPreview,
  listEmailHistory,
  previewPaymentRequest,
  sendPaymentRequest,
} from "@/lib/emailApi";
import { buildPaymentRequestEmail } from "@/lib/paymentRequestEmail";
import { formatGbpFromPence } from "@/types/database";
import type { EmailHistory } from "@/types/database";

export type PaymentRequestBookingSnapshot = {
  reference: string;
  venueName: string;
  contactName: string;
  email: string;
  performanceDate: string;
  performanceTime: string;
  address: string;
  feePence: number;
  dueDate: string;
  description: string;
};

/**
 * Preview-only: clamp the email's fixed 560px layout to the width of the
 * preview frame so it reads without sideways scrolling on phones. The real
 * email sent to clients is unchanged.
 */
const previewMobileHtml = (html: string) => {
  const style = `<style>table{width:100% !important;max-width:100% !important;}body{overflow-wrap:anywhere;}</style>`;
  return html.includes("</head>")
    ? html.replace("</head>", `${style}</head>`)
    : `${style}${html}`;
};

type Props = {
  bookingId: string;
  payLink: string;
  clientEmail: string | null | undefined;
  booking: PaymentRequestBookingSnapshot;
  /** Open preview automatically (e.g. after Save & Send) */
  autoPreview?: boolean;
  onMessage?: (msg: string) => void;
};

const PaymentRequestPanel = ({
  bookingId,
  payLink,
  clientEmail,
  booking,
  autoPreview,
  onMessage,
}: Props) => {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [history, setHistory] = useState<EmailHistory[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewNote, setPreviewNote] = useState<string | null>(null);

  const refreshHistory = useCallback(async () => {
    const res = await listEmailHistory(bookingId);
    if (res.ok) setHistory(res.data);
  }, [bookingId]);

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  const localPreview = useCallback((): EmailPreview => {
    const content = buildPaymentRequestEmail({
      clientName: booking.contactName,
      venueName: booking.venueName || "Your event",
      reference: booking.reference,
      performanceDate: booking.performanceDate,
      performanceTime: booking.performanceTime || "—",
      address: booking.address,
      feeDisplay: formatGbpFromPence(booking.feePence),
      dueDate: booking.dueDate,
      payUrl: payLink,
      description: booking.description,
      signOff: "Olivia Flemming",
    });
    return {
      kind: "payment_request",
      to_email: clientEmail || booking.email || "",
      subject: content.subject,
      html: content.html,
      text: content.text,
      pay_url: payLink,
      booking_reference: booking.reference,
    };
  }, [booking, clientEmail, payLink]);

  const loadPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    setPreviewNote(null);
    const res = await previewPaymentRequest(bookingId);
    setLoadingPreview(false);
    if (res.ok === false) {
      // Edge Function / Resend not required for Preview — show branded local HTML
      setPreview(localPreview());
      setShowPreview(true);
      setPreviewNote(
        `Live email preview unavailable (${res.error}). Showing branded local preview. Copy Link still works; Send needs Resend Edge secrets.`
      );
      return;
    }
    setPreview(res.data);
    setShowPreview(true);
  };

  useEffect(() => {
    if (autoPreview) {
      void loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPreview, bookingId]);

  const copyLink = async () => {
    setError(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payLink);
      } else {
        const ta = document.createElement("textarea");
        ta.value = payLink;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      onMessage?.("Payment link copied to clipboard.");
    } catch {
      setError(`Could not copy automatically. Select and copy this link: ${payLink}`);
      onMessage?.("Copy failed — link is shown below; select it manually.");
    }
  };

  const onSend = async () => {
    setSending(true);
    setError(null);
    const res = await sendPaymentRequest(bookingId);
    setSending(false);
    if (res.ok === false) {
      const msg =
        res.code === "resend_unconfigured" ||
        (res.error || "").toLowerCase().includes("resend")
          ? `${res.error} — Copy payment link still works without Resend.`
          : res.error;
      setError(msg);
      onMessage?.(msg);
      await refreshHistory();
      return;
    }
    onMessage?.(`Payment request sent to ${res.data.to_email || clientEmail || "client"}.`);
    await refreshHistory();
    setShowPreview(false);
  };

  const hasSent = history.some((h) => h.status === "sent");

  return (
    <div
      className="rounded-2xl border border-primary/30 bg-secondary/50 p-5 md:p-6 space-y-4"
      data-testid="payment-request-panel"
    >
      <div>
        <h3 className="font-playfair text-lg">Payment request</h3>
        <p className="text-xs text-white/40 font-inter mt-1 break-all select-all">{payLink}</p>
        <p className="text-xs text-white/35 font-inter mt-2">
          Send a branded email (Resend), copy the pay link, preview, or resend. Copy works even
          when email is not configured.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copyLink}
          className="rounded-full bg-white/10 border border-white/25 px-5 py-2.5 text-sm font-medium hover:border-primary hover:text-primary"
        >
          Copy payment link
        </button>
        <button
          type="button"
          onClick={onSend}
          disabled={sending}
          className="rounded-full bg-primary text-black px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {sending
            ? "Sending…"
            : hasSent
              ? "Resend payment email"
              : "Send payment request"}
        </button>
        <button
          type="button"
          onClick={loadPreview}
          disabled={loadingPreview}
          className="rounded-full border border-primary/40 text-primary px-5 py-2.5 text-sm hover:bg-primary/10 disabled:opacity-50"
        >
          {loadingPreview ? "Loading…" : "Preview email"}
        </button>
        <a
          href={payLink}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full border border-white/20 px-5 py-2.5 text-sm hover:border-primary hover:text-primary"
        >
          Open pay page
        </a>
      </div>

      {!clientEmail && (
        <p className="text-xs text-amber-200/90 font-inter">
          Add a client email on the booking before sending. You can still copy the payment link.
        </p>
      )}

      {previewNote && (
        <p className="text-xs text-amber-100/90 font-inter" role="status">
          {previewNote}
        </p>
      )}

      {error && (
        <p className="text-sm text-red-300 font-inter" role="alert">
          {error}
        </p>
      )}

      {showPreview && preview && (
        <div className="rounded-xl border border-white/15 overflow-hidden">
          <div className="px-4 py-3 bg-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-xs font-inter text-white/60 space-y-1">
              <p>
                <span className="text-white/40">To:</span> {preview.to_email || "—"}
              </p>
              <p>
                <span className="text-white/40">Subject:</span> {preview.subject}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyLink}
                className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-widest"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={onSend}
                disabled={sending}
                className="rounded-full bg-primary text-black px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
              >
                {sending ? "Sending…" : hasSent ? "Resend" : "Send this email"}
              </button>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-widest"
              >
                Close
              </button>
            </div>
          </div>
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={previewMobileHtml(preview.html)}
            className="w-full h-[420px] bg-black border-0"
          />
        </div>
      )}

      <div>
        <h4 className="text-xs uppercase tracking-widest text-white/45 font-inter mb-2">
          Email history
        </h4>
        {history.length === 0 ? (
          <p className="text-sm text-white/40 font-inter">No emails logged yet.</p>
        ) : (
          <ul className="space-y-2">
            {history.map((h) => (
              <li
                key={h.id}
                className="rounded-xl border border-white/10 px-3 py-2.5 text-xs font-inter text-white/65 flex flex-col sm:flex-row sm:justify-between gap-1"
              >
                <span>
                  {h.kind} · {h.to_email} ·{" "}
                  <span
                    className={
                      h.status === "sent"
                        ? "text-emerald-300"
                        : h.status === "failed"
                          ? "text-red-300"
                          : "text-amber-200"
                    }
                  >
                    {h.status}
                  </span>
                  {h.error_message ? ` — ${h.error_message}` : ""}
                </span>
                <span className="text-white/35 whitespace-nowrap">
                  {h.sent_at
                    ? new Date(h.sent_at).toLocaleString("en-GB")
                    : new Date(h.created_at).toLocaleString("en-GB")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PaymentRequestPanel;
